'use client';

import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';

/**
 * Read a match percentage out of the number input.
 *
 * `type=number` still reports a plain string, and reports `''` both for a
 * cleared box and for a partial entry like `-` — and `Number('')` is 0, so a
 * cleared box would silently mean "hide nothing but exact duplicates is off,
 * hide everything". Falls back to the value already in state instead of to 0
 * or `NaN`, then clamps to the range the columns and every request accept
 * (`between:0,100` on an `unsignedTinyInteger`).
 *
 * Exported for `tests/unit/undeclared-match-threshold.test.ts`.
 */
export function clampMatchThreshold(raw: string, fallback: number): number {
  const parsed = Number.parseInt(raw, 10);

  if (Number.isNaN(parsed)) return fallback;

  return Math.min(100, Math.max(0, parsed));
}

interface MatchThresholdSliderProps {
  /** Ties the visible label to the slider; also the slider's DOM id. */
  id: string;
  label: ReactNode;
  /** Accessible name for the slider (the label may be rich content). */
  ariaLabel: string;
  /** Accessible name for the number box beside it. */
  inputAriaLabel: string;
  value: number;
  onValueChange: (value: number) => void;
  disabled?: boolean;
  /** Smaller label and input, for popovers and dialogs. */
  compact?: boolean;
  className?: string;
}

/**
 * The one 0-100 match-percentage control: a number box beside a bounded 1%
 * slider with 0% / 100% end labels.
 *
 * Shared by the template-wide setting, the per-field override and both places
 * a rejection's radius is set, so the four read as the same lever and none
 * drifts on the step, the clamp or the RTL handling.
 */
export function MatchThresholdSlider({
  id,
  label,
  ariaLabel,
  inputAriaLabel,
  value,
  onValueChange,
  disabled,
  compact = false,
  className
}: MatchThresholdSliderProps) {
  return (
    <div className={cn('space-y-3', className)}>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <label
          htmlFor={id}
          className={cn(compact ? 'text-xs' : 'text-sm font-medium')}
        >
          {label}
        </label>
        {/* Typed as well as dragged: 1% steps over 0-100 is a hundred
            positions of precision, and someone who already knows the number
            wants to enter it rather than hunt for it. Clamped on change AND
            on blur, because a spinner arrow and a paste are two different
            events. */}
        <div className='flex items-center gap-1.5'>
          <Input
            type='number'
            inputMode='numeric'
            min={0}
            max={100}
            step={1}
            value={value}
            onChange={(event) =>
              onValueChange(clampMatchThreshold(event.target.value, value))
            }
            onBlur={(event) =>
              onValueChange(clampMatchThreshold(event.target.value, value))
            }
            disabled={disabled}
            aria-label={inputAriaLabel}
            className={cn(
              'w-16 tabular-nums',
              compact ? 'h-7 text-xs' : 'h-8 text-sm'
            )}
          />
          <span className='text-muted-foreground text-sm'>%</span>
        </div>
      </div>

      {/* Bounded rather than page-wide: this is a 0-100 control, and
          stretched across a 1500px viewport its 1% steps land pixels apart.
          The end labels sit in LOGICAL order — 0% first in the DOM — so RTL
          puts the minimum on the right, which is where Radix's own `dir`
          handling starts the track. No direction branch. */}
      <div className='flex max-w-md items-center justify-between gap-3'>
        <span className='text-muted-foreground text-xs tabular-nums'>0%</span>
        <Slider
          id={id}
          min={0}
          max={100}
          step={1}
          value={[value]}
          onValueChange={([next]) => onValueChange(next ?? value)}
          disabled={disabled}
          aria-label={ariaLabel}
          className='flex-1'
        />
        <span className='text-muted-foreground text-xs tabular-nums'>100%</span>
      </div>
    </div>
  );
}
